import { useState, useCallback } from 'react';

// Helper function to get auth token (same pattern as useFinanceAPI)
const getAuthToken = (): string | null => {
  try {
    const storedTokens = localStorage.getItem('auth_tokens');
    if (storedTokens) {
      const tokens = JSON.parse(storedTokens);
      return tokens.access_token;
    }
  } catch (error) {
    console.error('[useDocumentShares] Failed to load auth token:', error);
  }
  return null;
};

export interface DocumentShare {
  id: number;
  document_id: number | string;
  document_type: string;
  document_number: string;
  document_total: number;
  recipient_email: string;
  recipient_name: string | null;
  status: 'active' | 'expired' | 'revoked';
  expires_at: Date | null;
  max_access_count: number | null;
  access_count: number;
  created_at: Date;
  created_by: number;
  share_url: string;
  permissions: string[];
  last_accessed_at: Date | null;
}

interface LoadSharesParams {
  status?: string;
  search?: string;
  document_id?: number | string;
  page?: number;
  pageSize?: number;
}

interface CreateShareParams {
  document_id: number | string;
  document_type?: string;
  recipient_email: string;
  recipient_name?: string;
  expires_in_days?: number;
  never_expires?: boolean;
  max_access_count?: number;
  require_password?: boolean;
  password?: string;
  ip_whitelist?: string[];
  permissions?: string[];
  custom_message?: string;
}

export function useDocumentShares() {
  const [shares, setShares] = useState<DocumentShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 25,
    hasMore: false,
  });

  const loadShares = useCallback(async (params: LoadSharesParams = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const queryParams = new URLSearchParams();
      
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.search) queryParams.append('recipient_email', params.search);
      if (params.document_id) queryParams.append('document_id', params.document_id.toString());
      if (params.page) queryParams.append('offset', ((params.page - 1) * (params.pageSize || 25)).toString());
      if (params.pageSize) queryParams.append('limit', params.pageSize.toString());

      const response = await fetch(`/api/v1/finance/shares?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load document shares');
      }

      const data = await response.json();
      
      setShares(data.data.shares);
      setPagination({
        total: data.data.pagination.total,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
        hasMore: data.data.pagination.has_more,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('Error loading shares:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createShare = useCallback(async (params: CreateShareParams) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await fetch('/api/v1/finance/shares', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create share');
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      console.error('Error creating share:', err);
      throw err;
    }
  }, []);

    const revokeShare = useCallback(async (shareId: number) => {
    try {
      const token = getAuthToken();
      
      const response = await fetch(`/api/v1/finance/shares/${shareId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to revoke share');
      }

      return true;
    } catch (err) {
      console.error('Error revoking share:', err);
      throw err;
    }
  }, []);

  const reinstateShare = useCallback(async (shareId: number) => {
    try {
      const token = getAuthToken();
      
      const response = await fetch(`/api/v1/finance/shares/${shareId}/reinstate`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to reinstate share');
      }

      return true;
    } catch (err) {
      console.error('Error reinstating share:', err);
      throw err;
    }
  }, []);

  const getShareDetails = useCallback(async (shareId: number) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await fetch(`/api/v1/finance/shares/${shareId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load share details');
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      console.error('Error loading share details:', err);
      throw err;
    }
  }, []);

  return {
    shares,
    loading,
    error,
    pagination,
    loadShares,
    createShare,
    revokeShare,
    reinstateShare,
    getShareDetails,
  };
}
